import {
	resolveTableViewState,
	Table,
	TableCard,
	type TableRowAction,
	TableRowActions,
	TableStateRow,
} from "@components";
import { Archive, Copy04, Edit01, Link02 } from "@untitledui/icons";
import type { ReactNode } from "react";
import { FORMS_LIST_COLUMNS } from "../constants";
import type { PaginatedChecklistTemplateItem } from "../types";
import { TemplateStatus } from "../types/template";
import { formatLastUpdate } from "../utils";
import type { FormStatusTab } from "./form-status-tabs";

type FormsListTableProps = {
	items: PaginatedChecklistTemplateItem[];
	isFetching: boolean;
	isError?: boolean;
	onRetry?: () => void;
	footer?: ReactNode;
	statusTab: FormStatusTab;
	onArchive: (item: PaginatedChecklistTemplateItem) => void;
	onCopyLink: (item: PaginatedChecklistTemplateItem) => void;
	onDuplicate: (item: PaginatedChecklistTemplateItem) => void;
	onEdit: (item: PaginatedChecklistTemplateItem) => void;
	onRowClick: (item: PaginatedChecklistTemplateItem) => void;
};

type FormsListRowCallbacks = Pick<
	FormsListTableProps,
	"onArchive" | "onCopyLink" | "onDuplicate" | "onEdit"
>;

function FormsListTableHead({
	column,
}: {
	column: (typeof FORMS_LIST_COLUMNS)[number];
}) {
	return (
		<Table.Head
			id={column.id}
			isRowHeader={FORMS_LIST_COLUMNS.indexOf(column) === 0}
			key={column.id}
			label={column.label}
			style={{
				...(!!column?.minWidth && {
					minWidth: `${column.minWidth}px`,
				}),
			}}
		/>
	);
}

function buildFormsListRowActions(
	item: PaginatedChecklistTemplateItem,
	statusTab: FormStatusTab,
	{ onArchive, onCopyLink, onDuplicate, onEdit }: FormsListRowCallbacks
): TableRowAction[] {
	const isActive = statusTab === TemplateStatus.Active;
	const isDraft = statusTab === TemplateStatus.Draft;
	const actions: TableRowAction[] = [];

	if (isActive) {
		actions.push({
			id: "copy-link",
			label: "Copiar link",
			icon: Link02,
			isDisabled: !item.checklistUrl,
			onClick: () => {
				onCopyLink(item);
			},
		});
	}

	if (isActive || isDraft) {
		actions.push({
			id: "duplicate",
			label: "Duplicar",
			icon: Copy04,
			onClick: () => {
				onDuplicate(item);
			},
		});
	}

	if (isDraft) {
		actions.push({
			id: "edit",
			label: "Editar",
			icon: Edit01,
			onClick: () => {
				onEdit(item);
			},
		});
	}

	if (isActive || isDraft) {
		actions.push({
			id: "archive",
			label: "Arquivar",
			icon: Archive,
			onClick: () => {
				onArchive(item);
			},
		});
	}

	return actions;
}

function FormsListTableRow({
	item,
	statusTab,
	onRowClick,
	...callbacks
}: FormsListRowCallbacks & {
	item: PaginatedChecklistTemplateItem;
	statusTab: FormStatusTab;
	onRowClick: (item: PaginatedChecklistTemplateItem) => void;
}) {
	return (
		<Table.Row
			className="cursor-pointer hover:bg-secondary_subtle"
			columns={FORMS_LIST_COLUMNS}
			id={item.id}
			onAction={() => {
				onRowClick(item);
			}}
		>
			<Table.Cell className="font-medium">{item.name}</Table.Cell>
			<Table.Cell className="whitespace-nowrap">
				{item.templateAnsweredCount}
			</Table.Cell>
			<Table.Cell className="whitespace-nowrap">
				{formatLastUpdate(item.updatedAt ?? item.createdAt)}
			</Table.Cell>
			<Table.Cell>
				<TableRowActions
					actions={buildFormsListRowActions(item, statusTab, callbacks)}
					label={`Ações do formulário ${item.name}`}
				/>
			</Table.Cell>
		</Table.Row>
	);
}

export function FormsListTable({
	items,
	isFetching,
	isError = false,
	onRetry,
	footer,
	statusTab,
	onArchive,
	onCopyLink,
	onDuplicate,
	onEdit,
	onRowClick,
}: FormsListTableProps) {
	const view = resolveTableViewState({
		itemCount: items.length,
		isLoading: isFetching,
		isError,
	});

	return (
		<TableCard.Root variant="listing">
			<Table
				aria-label="Lista de formulários"
				isRefreshing={view.isRefreshing}
				refreshingLabel="Atualizando formulários"
				selectionMode="none"
			>
				<Table.Header columns={FORMS_LIST_COLUMNS}>
					{(column) => <FormsListTableHead column={column} />}
				</Table.Header>

				<Table.Body>
					{view.view === "data" ? (
						<>
							{view.hasRefreshError ? (
								<TableStateRow
									colSpan={FORMS_LIST_COLUMNS.length}
									onRetry={onRetry}
									state="refresh-error"
								/>
							) : null}
							{items.map((item) => (
								<FormsListTableRow
									item={item}
									key={item.id}
									onArchive={onArchive}
									onCopyLink={onCopyLink}
									onDuplicate={onDuplicate}
									onEdit={onEdit}
									onRowClick={onRowClick}
									statusTab={statusTab}
								/>
							))}
						</>
					) : (
						<TableStateRow
							colSpan={FORMS_LIST_COLUMNS.length}
							emptyContent="Nenhum resultado encontrado."
							errorMessage="Não foi possível carregar os formulários."
							loadingLabel="Carregando formulários"
							onRetry={onRetry}
							state={view.view}
						/>
					)}
				</Table.Body>
			</Table>
			{footer}
		</TableCard.Root>
	);
}
